import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  GridItem,
  Box,
  Image,
  Flex,
  RadioGroup,
  Stack,
  HStack,
  Button,
  IconButton,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import "../../../App.css";
import APTextInput from "../../../components/APTextInput";
import APMainButton from "../../../components/APMainButton";
import APIconButton from "../../../components/APIconButton";
import { FullScreenContext } from "../../../contexts/FullScreenContext";
import { OperatorModel } from "../../../types";
import { useSettingsContext } from "../../../contexts/SettingsContext";
import { FormattedMessage, useIntl } from "react-intl";
import APButton from "../../../components/APButton";
import APRadio from "../../../components/APRadio";
import { MinusIcon, PlusSquareIcon } from "@chakra-ui/icons";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import APRadioGroup from "../../../components/APRadioGroup";
import {INITIAL_DIFFICULTY, TAGS_LIST} from "../../../constants";
import NavMenu from "../../../components/NavMenu";

interface InitialStepProps {
  onStart: (
    level: string,
    difficulty: number,
    operation: OperatorModel,
    problemsCount: number
  ) => void;
  demo?: boolean;
  level: string;
  sdk: any;
}

export default function InitialStep(props: InitialStepProps) {
  const { onStart, demo, sdk } = props;
  const intl = useIntl();

  const { setProfileOpen, setSettingModalOpen, onSetSettings, skin, settings } = useSettingsContext();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const navigate = useNavigate();

  const [level, setLevel] = useState<string>();
  const [selectedLevel, setSelectedLevel] = useState<string>();
  const [difficulty, setDifficulty] = useState<number>(INITIAL_DIFFICULTY);
  const [category, setCategory] = React.useState<OperatorModel>(
    OperatorModel.ADDITION
  );
  const [problemsCount, setProblemsCount] = useState(5);

  useEffect(() => {
    level && sessionStorage.setItem("user_current_level", level)
  }, [level]);
  useEffect(() => {
    selectedLevel && sessionStorage.setItem("user_selected_level", selectedLevel)
  }, [selectedLevel]);

  useEffect(() => {
    if (props.sdk) {
      setIsLoggedIn(props.sdk._firebase.isLoggedIn());
    }
    const sessionLevel = sessionStorage.getItem("user_current_level");
    const sessionSelectedLevel = sessionStorage.getItem("user_selected_level");
    sessionLevel && setLevel(sessionLevel);
    sessionSelectedLevel && setSelectedLevel(sessionSelectedLevel);
    settings?.level && setLevel(settings.level);
    settings?.selectedLevel && setSelectedLevel(settings.selectedLevel);
    settings?.difficulty && setDifficulty(settings?.difficulty);
  }, [props.sdk, settings]);

  useEffect(() => {
    if (isLoggedIn && !level) {
      const getUserSettings = async () => {
        try{
          const user = await props.sdk.getDataManager().getUserDoc();
          if (user?.level >= 3 && user.level <= 6) {
            setLevel(TAGS_LIST[user.level - 3].value);
            setSelectedLevel(TAGS_LIST[user.level - 3].value);
          }
        } catch(err) {}
      }
      getUserSettings();
    }
  }, [props.sdk, isLoggedIn, level]);

  useEffect(() => {
    function handleResize() {
      setScreenWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOpenSettings = () => {
    setSettingModalOpen(true);
  };

  const handleStart = () => {
    level && onStart(level, difficulty, category, problemsCount);
  };

  const decreaseProblemsCount = () => {
    setProblemsCount((count) => count - 1);
  };

  const increaseProblemsCount = () => {
    setProblemsCount((count) => count + 1);
  };

  return (
    <FullScreenContext.Consumer>
      {({ fullScreen, toggleFullScreen }) => (
        <div
          className={
            "exercise-screen" +
            (fullScreen || screenWidth < 764 ? " full-screen" : "")
          }
          style={{
            backgroundColor: skin?.data?.color?.iv,
            backgroundImage: skin?.data?.background && `url(${skin?.data?.background})`
          }}
        >
          <Grid
            className="exercise-actions"
            templateColumns="repeat(1, 1fr)"
            gap={2}
          >
            <GridItem>
              <Box
                sx={{
                  display: "flex",
                  mt: "2",
                  justifyContent: "space-between",
                  width: "100%",
                  color: skin?.data?.headingColor
                }}
              >
                {screenWidth > 764 && (
                  <Flex flexWrap={"nowrap"}>
                    <Box
                      sx={{
                        fontSize: isLargeScreen ? "30px" : "24px",
                        marginTop: isLargeScreen ? "-6px" : "-5px",
                      }}
                    >
                      <FormattedMessage id="app-header-title" defaultMessage={"Exercice interactif"}/>{" "}
                    </Box>
                    <Box
                      sx={{
                        fontWeight: 900,
                        fontSize: isLargeScreen ? "30px" : "24px",
                        marginTop: isLargeScreen ? "-6px" : "-5px",
                      }}
                    >
                      <FormattedMessage
                        id="app-header-sub-title"
                        defaultMessage={"Operations sur les nombres décimaux"}
                      />
                    </Box>
                  </Flex>
                )}
              </Box>
              <NavMenu
                openProfile={() => setProfileOpen(true)}
                openSettings={() => handleOpenSettings()}
                profileBtnTabIndex={1}
                fullscreenBtnTabIndex={2}
                settingsBtnTabIndex={3}
              />
            </GridItem>
          </Grid>
          <Box
            className="setup-content"
            sx={{
              width: { base: "calc(100% - 24px)", md: "80%" },
              margin: "auto",
              mt: { base: "1.26rem", md: "2.5rem" },
              maxWidth: "1008px",
            }}
          >
            <Grid
              templateColumns={
                screenWidth > 764 ? "repeat(2, 1fr)" : "repeat(1, 1fr)"
              }
              gap={2}
            >
              {/* <GridItem>
                {screenWidth < 764 && (
                  <Box
                    sx={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      border: "solid 2px #f5f3ef",
                      padding: "24px",
                      pb: "0",
                      position: "relative",
                      mb: { base: "2.375rem", md: "7" },
                      mx: "1rem",
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        fontSize: "21px",
                        fontWeight: "bold",
                      }}
                    >
                      <FormattedMessage
                        id="interactive-vocabulary-exercise"
                        defaultMessage={"Exercice interactif de vocabulaire"}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <img src="illustrations.svg" alt="" />
                    </Box>
                  </Box>
                )}
              </GridItem> */}
              <Box
                sx={{
                  background: "#ffffff",
                  borderRadius: "24px",
                  border: "solid 2px #f5f3ef",
                  padding: isLargeScreen ? "30px" : "24px",
                  position: "relative",
                  marginTop: screenWidth > 764 ? 0 : "20px",
                  marginBottom: screenWidth > 764 ? 0 : "20px",
                  marginLeft: screenWidth > 764 ? "20px" : 0,
                }}
              >
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "26px" : "21px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  <FormattedMessage
                    id="school-level"
                    defaultMessage={"Niveau scolaire"}
                  />
                </Box>

                <Grid templateColumns="repeat(1, 1fr)" gap={2} sx={{ mt: "5" }}>
                  {TAGS_LIST.map((tag) => (
                    <GridItem sx={{ mt: "2" }}>
                      <APButton
                        colorScheme={
                          tag.value === selectedLevel ? "primary" : "secondary"
                        }
                        size="lg"
                        key={tag.value}
                        sx={{
                          width: "100%",
                        }}
                        onClick={async () => {
                          setLevel(tag.value);
                          setDifficulty(INITIAL_DIFFICULTY);
                          setSelectedLevel(tag.value);
                          onSetSettings({
                            ...settings,
                            difficulty: INITIAL_DIFFICULTY,
                            level: tag.value,
                            selectedLevel: tag.value
                          });
                          await sdk.getDataManager().saveSettings({data: {
                            difficulty: INITIAL_DIFFICULTY,
                            level: tag.value,
                            selectedLevel: tag.value
                          }});
                        }}
                      >
                        <FormattedMessage
                          id={"primary-label"}
                          defaultMessage={"Primaire"}
                        /> {tag.value.replace("primary_", "")}
                      </APButton>
                    </GridItem>
                  ))}
                </Grid>
              </Box>
              <Box
                sx={{
                  background: "#ffffff",
                  borderRadius: "24px",
                  border: "solid 2px #f5f3ef",
                  padding: isLargeScreen ? "30px" : "24px",
                  position: "relative",
                  marginTop: screenWidth > 764 ? 0 : "20px",
                  marginBottom: screenWidth > 764 ? 0 : "20px",
                  marginLeft: screenWidth > 764 ? "20px" : 0,
                }}
              >
                <Box
                  sx={{
                    fontSize: isLargeScreen ? "26px" : "21px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  <FormattedMessage
                    id="operations"
                    defaultMessage={"Operations"}
                  />
                </Box>
                <Box sx={{ mt: isLargeScreen ? "40px" : "32px" }}>
                  <APRadioGroup
                    defaultValue="1"
                    onChange={(value: OperatorModel) => setCategory(value)}
                    value={category}
                  >
                    <Stack spacing={isLargeScreen ? 6 : 5}>
                      <APRadio value={OperatorModel.ADDITION} size="lg" sx={{
                        fontSize: isLargeScreen ? "1.26rem" : "1rem"
                      }}>
                        <Text sx={{fontSize: isLargeScreen ? "1.4rem" : "1.125rem", ml: "3px"}}>
                          <FormattedMessage id="addition" defaultMessage={"Addition"}/>
                        </Text>
                      </APRadio>
                      <APRadio value={OperatorModel.SUBSTRACTION} size="lg">
                        <Text sx={{fontSize: isLargeScreen ? "1.4rem" : "1.125rem", ml: "3px"}}>
                          <FormattedMessage id="substraction" defaultMessage={"Soustraction"}/>
                        </Text>
                      </APRadio>
                    </Stack>
                  </APRadioGroup>
                </Box>

                <Box sx={{ mt: isLargeScreen ? "4rem" : "3rem" }}>
                  <Box
                    sx={{
                      fontSize: isLargeScreen ? "26px" : "21px",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <FormattedMessage
                      id="number-of-questions"
                      defaultMessage={"Nombre de questions"}
                    />
                  </Box>
                  <HStack sx={{ justifyContent: "center", mt: isLargeScreen ? "40px" : "32px" }}>
                    <IconButton
                      icon={<AiOutlineMinus size={isLargeScreen ? 20 : 16} />}
                      aria-label="minus icon"
                      onClick={decreaseProblemsCount}
                      disabled={problemsCount === 1}
                      sx={{
                        w: isLargeScreen ? "50px" : "40px",
                        h: isLargeScreen ? "50px" : "40px",
                        "svg": {
                          width: "100px"
                        }
                      }}
                    />
                    <Text sx={{
                      fontSize: isLargeScreen ? "30px" : "24px",
                      fontWeight: "bold",
                      ml: isLargeScreen ? "0.6rem" : "0.5rem"
                    }}>
                      {problemsCount}
                    </Text>
                    <IconButton
                      icon={<AiOutlinePlus size={isLargeScreen ? 20 : 16} />}
                      aria-label="plus icon"
                      onClick={increaseProblemsCount}
                      sx={{
                        w: isLargeScreen ? "50px" : "40px",
                        h: isLargeScreen ? "50px" : "40px",
                        ml: isLargeScreen ? "0.6rem" : "0.5rem"
                      }}
                    />
                  </HStack>
                </Box>
              </Box>
            </Grid>
            <Box sx={{ mt: 4, mb: isLargeScreen ? 0 : 6, display: "flex", justifyContent: "center" }}>
              <APButton disabled={!level} colorScheme={"primary"} size="lg" onClick={handleStart}>
                <FormattedMessage id="start-button" defaultMessage={`Commencer`}/>
              </APButton>
            </Box>
          </Box>
        </div>
      )}
    </FullScreenContext.Consumer>
  );
}
